<template>
  
  <div>

    <div v-if="loading"><i class="spinner circle loading blue icon"></i> Carregando ...</div>

    <div v-else class="ui grid animated fadeIn">      

      <div v-if="!cert.fdata" class="sixteen wide column">
        <div class="ui segment" style="min-height: 350px;">
          <p class="grey text m-t-10">
            <i class="info circle icon"></i> Dados financeiros ainda não informados
          </p>
        </div>
      </div>

      <div v-if="cert.fdata" class="sixteen wide column">
        <div class="ui segment">
          <h4 class="ui dividing header">
            Dados do Laudo
            <span v-if="finalityLabel" class="ui label">{{ finalityLabel }}</span>
            <span v-if="cert.customerInvoiceId" class="ui teal label clickable" @click="onShowCustomerInvoice()">
              Fechamento <div class="detail">{{ cert.customerInvoiceId }}</div>
            </span>
          </h4>
          <div class="ui form">           
            <div class="fields">
              <div class="three wide field">
                <label class="form-label">Número</label>
                {{ cert.id }}
              </div>
              <div class="three wide field">
                <label class="form-label">Data</label>
                {{ $formatDate(cert.date) }}
              </div>              
              <div class="three wide field">
                <label class="form-label">Clínica</label>
                {{ cert.customer.shortName }}
              </div>
              <div class="five wide field">
                <label class="form-label">Colaborador</label>
                {{ cert.user.name.toUpperCase() + ' ' + cert.user.lastName.toUpperCase() }}
              </div>
            </div>
            <div class="fields">
              <div class="three wide field">
                <label class="form-label">Período Pagamento</label>
                {{ paymentPeriod.description }}
              </div>
              <div class="three wide field">
                <label class="form-label">Forma Pagamento</label>
                <i :class="paymentType.icon"></i>
                {{ paymentType.description }}
              </div>                
              <div class="three wide field">
                <label class="form-label">Pagamento para</label>
                {{ paymentToLabel }}
              </div>
              <div class="three wide field">
                <label class="form-label">Emite NF</label>
                {{ issuesInvoiceLabel }}
              </div>     
              <div class="four wide field">
                <label class="form-label">Serviço</label>
                {{ cert.service.description }}                
              </div>            
            </div>
            <div class="fields">
              <div class="three wide field">
                <label class="form-label">Tipo Serviço</label>
                <service-type-icon :icon="cert.serviceType.icon" />
                {{ cert.serviceType.description }}
              </div>
              <div class="three wide field">
                <label class="form-label">Valor do Serviço</label>
                R$ {{ $formatCurrency(cert.fdata.serviceValue) }}                
              </div>
              <div class="three wide field">
                <label class="form-label">Valor em Dinheiro</label>
                R$ {{ $formatCurrency(cert.fdata.moneyValue) }}        
              </div>
              <div class="three wide field">
                <label class="form-label">Valor do Laudo</label>
                R$ {{ $formatCurrency(cert.value) }}                
              </div>
              <div class="four wide field">
                <label class="form-label">Repasse</label>
                R$ {{ $formatCurrency(cert.fdata.payback) }}
              </div>                         
            </div>
            <div class="fields">
              <div class="three wide field">
                <label class="form-label">Impostos</label>
                R$ {{ $formatCurrency(cert.fdata.taxes) }}                
              </div>   
              <div class="three wide field">
                <label class="form-label">Valor Cartão</label>
                R$ {{ $formatCurrency(cert.fdata.cardTotalValue) }}                
              </div>
              <div class="three wide field">
                <label class="form-label">Taxa Cartão</label>
                R$ {{ $formatCurrency(cert.fdata.cardFeeValue) }}                
              </div>
              <div class="three wide field">
                <label class="form-label">Desconto Simavet</label>
                R$ {{ $formatCurrency(cert.fdata.companyDiscount) }}                
              </div>
              <div class="four wide field">
                <label class="form-label">Desconto Colaborador</label>
                R$ {{ $formatCurrency(cert.fdata.employeeDiscount) }}                
              </div>                        
            </div>
            <div class="fields">
              <div class="three wide field">
                <label class="form-label">Valor Líquido</label>
                R$ {{ $formatCurrency(cert.fdata.liquidValue) }}                
              </div>  
              <div class="three wide field">
                <label class="form-label">Valor Base Colaborador</label>
                R$ {{ $formatCurrency(cert.fdata.employeeBaseValue) }}                
              </div>  
              <div class="three wide field">
                <label class="form-label">% Colaborador</label>
                {{ $formatCurrency(cert.fdata.employeePercent) }}                
              </div>
              <div class="three wide field">
                <label class="form-label">Valor Colaborador</label>
                R$ {{ $formatCurrency(cert.fdata.employeeValue) }}                
              </div>
              <div class="four wide field">
                <label class="form-label">Valor Antecipado</label>
                R$ {{ $formatCurrency(cert.anticipationValue) }}
                <payment-type v-if="cert.anticipationValue" :paymentType="cert.anticipationType" :iconOnly="true" class="m-l-5" />            
              </div>
            </div>
            <div class="fields">
              <div class="three wide field">
                <label class="form-label">Total Recebido</label>
                R$ {{ $formatCurrency(cert.fdata.receivedValue) }}                
              </div>
              <div class="three wide field">
                <label class="form-label">Recebido pelo Colaborador</label>
                R$ {{ $formatCurrency(cert.fdata.liquidReceivedValue) }}                
              </div>
              <div class="three wide field">
                <label class="form-label">Total a Pagar Colaborador</label>
                R$ {{ $formatCurrency(cert.fdata.payableValue) }}                
              </div>              
              <div class="three wide field">
                <label class="form-label">Total a Receber do Cliente</label>
                R$ {{ $formatCurrency(cert.fdata.invoiceableValue) }}                
              </div>
            </div>
          </div>
        </div>
      </div>       

      <div v-if="cert.fdata" class="sixteen wide column">
        <div class="ui segment">
          <h4 class="ui dividing header">
            Recebimento
          </h4>
          <table class="ui celled selectable tablet stackable table">
            <thead v-if="$isDesktop">
              <tr>
                <th>Parcela</th>
                <th>Valor</th>
                <th>Forma de Pagamento</th>
                <th>Desconto Simavet</th>
                <th>Desconto Colaborador</th>
                <th>Valor Recebido</th>
                <th>Recebido por</th>
                <th>Valor a Receber</th>
              </tr>  
            </thead>
            <tbody>
              <tr v-for="(receivable, index) in receivables" @click="onShowReceivable(index)">
                <td>
                  <strong class="mobile-only">Parcela: </strong> {{ receivable.sequence }}                  
                </td>
                <td>
                  <strong class="mobile-only">Valor: </strong> R$ {{ $formatCurrency(receivable.value) }}   
                </td>
                <td :class="{ negative: (receivable.paymentType == 'waiting_transfer') }">
                  <strong class="mobile-only">Forma de Pagamento: </strong> 
                  <i :class="receivable.paymentTypeData.icon"></i>
                  {{ receivable.paymentTypeData.description }}
                </td>  
                <td>
                  <strong class="mobile-only">Desconto Simavet: </strong> R$ {{ $formatCurrency(receivable.companyDiscount) }}
                </td>
                <td>
                  <strong class="mobile-only">Desconto Colaborador: </strong> R$ {{ $formatCurrency(receivable.employeeDiscount) }}
                </td>            
                <td>
                  <strong class="mobile-only">Valor Recebido: </strong> R$ {{ $formatCurrency(receivable.receivedValue) }}
                </td>
                <td>
                  <strong class="mobile-only">Recebido por: </strong>
                  {{ receivable.user ? receivable.user.name + ' ' + receivable.user.lastName : '' }}
                </td>
                <td>
                  <strong class="mobile-only">Valor a Receber: </strong> R$ {{ $formatCurrency(receivable.invoiceableValue) }}
                </td>
              </tr>                
            </tbody>
          </table>
        </div>
      </div>      

    </div>   
    
    <receivable-modal ref="receivableModal" :receivable="selectedReceivable" />
    
  </div>   

</template>

<script>

  import AppBrowser from "@/utils/browser"
  import FinancialService from "@/services/financial-service"
  import { PaymentType, ServiceTypeIcon } from "@/components"  
  import ReceivableModal from "./receivable-modal"

  export default {
    
    name: "CertFinancialTab",

    data() {
      return {
        loading: true,
        paymentPeriod: {},
        paymentType: {},
        paymentToLabel: "",
        issuesInvoiceLabel: "",
        receivables: [],
        selectedReceivable: null,
      }        
    },

    props: ["cert"],

    components: {
      PaymentType,
      ServiceTypeIcon,
      ReceivableModal,
    },

    created() {

      let customer = this.cert.customer
      const undefinedLabel = "Não definido"

      this.paymentPeriod.description = undefinedLabel
      this.paymentType.description = undefinedLabel
      this.paymentToLabel = undefinedLabel
      this.issuesInvoiceLabel = undefinedLabel

      if (customer.paymentPeriod)        
        this.paymentPeriod = FinancialService.PAYMENT_PERIOD[customer.paymentPeriod]  
        
      if (customer.paymentType)        
        this.paymentType = FinancialService.PAYMENT_TYPE[customer.paymentType]

      if (customer.paymentTo)  
        this.paymentToLabel = customer.paymentTo == "company" ? "Simavet" : "Colaborador"

      if (typeof customer.issuesInvoice == "boolean")  
        this.issuesInvoiceLabel = customer.issuesInvoice ? "Sim" : "Não"

      this.receivables = this.cert.receivables.slice()
      
      this.receivables.forEach((receivable) => receivable.paymentTypeData = FinancialService.PAYMENT_TYPE[receivable.paymentType])

      this.loading = false
    },

    computed: {

      finalityLabel() {

        let label = ""

        if (this.cert.finality == "control") {
          label = "Controle"
        } else if (this.cert.finality == "revaluation") {
          label = "Reavaliação"
        }

        return label
      },
    },

    methods: {

      onShowCustomerInvoice() {
        AppBrowser.openNewTab(`/financial/customer-invoices/${this.cert.customerInvoiceId}/show`)
      },

      onShowReceivable(index) {
        this.selectedReceivable = this.receivables[index]
        this.$refs.receivableModal.show()
      },
    },
  }

</script>